import { createElement } from "react";
import { createRoot, type Root } from "react-dom/client";

import { EmbeddedJanApp } from "./EmbeddedJanApp";
import { EMBED_CSS } from "./embed-styles";
import { captureAttribution } from "../pages/jan-public/utils/attribution";

// Capture marketing attribution as early as possible — the instant this embed
// script loads on the host page, before any in-site navigation can strip the
// params off the URL. Persisted to sessionStorage; read back when the session
// is minted on first submit.
captureAttribution();

/**
 * `<novonote-jan variant="..." height="...">` — the embeddable widget host
 * NovoPsych marketing pastes into WordPress pages. It owns an open shadow root
 * so the host page's CSS can't restyle the widget (and vice versa): the
 * compiled Tailwind + `:host` token block are injected as a single <style>.
 */
class NovonoteJan extends HTMLElement {
  private root: Root | null = null;

  connectedCallback() {
    // connectedCallback fires on every (re)insertion. The shadow root survives
    // a disconnect but disconnectedCallback unmounts the React root, so bail
    // only when the root is still live — otherwise recreate it below.
    if (this.root) return;

    const variant = this.getAttribute("variant");
    // A bare number means pixels; anything else (e.g. "100%", "50vh") is a
    // full CSS length and must be used verbatim.
    const rawHeight = this.getAttribute("height") ?? "640";
    const height = /^\d+(\.\d+)?$/.test(rawHeight)
      ? `${rawHeight}px`
      : rawHeight;

    // An element can only have one shadow root; reuse it across re-insertions.
    const shadow = this.shadowRoot ?? this.attachShadow({ mode: "open" });
    // Clear any leftover <style>/mount from a previous connection before we
    // rebuild, so re-attachment doesn't stack duplicate nodes.
    shadow.replaceChildren();

    const style = document.createElement("style");
    style.textContent = EMBED_CSS;
    shadow.appendChild(style);

    const mount = document.createElement("div");
    mount.style.height = height;
    shadow.appendChild(mount);

    if (!variant) {
      mount.textContent = "novonote-jan: missing required `variant` attribute";
      return;
    }

    this.root = createRoot(mount);
    this.root.render(createElement(EmbeddedJanApp, { variant }));
  }

  disconnectedCallback() {
    this.root?.unmount();
    this.root = null;
  }
}

if (!customElements.get("novonote-jan")) {
  customElements.define("novonote-jan", NovonoteJan);
}
